import api from "api/axios";

// LinkedIn outreach touches (step 6). The server decides every state and every "today" (Asia/Karachi); the browser never
// computes a day rule. Dates ("2026-11-04") are plain calendar dates and are shown as written.

export type TouchKind = "connection_request" | "acceptance_message" | "follow_up";
export type TouchAction = "sent" | "accepted" | "replied" | "no_profile" | "close" | "undo" | "reopen" | "set_contact";

export interface TouchView {
  id: number;
  opportunity_id: number;
  rfp_title: string | null;
  prime_company_id: number;
  company_name: string | null;
  prime_contact_id: number | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_title: string | null;
  profile_url: string | null;
  kind: TouchKind;
  stored_state: string;
  state: "planned" | "done" | "skipped" | "stopped";
  display: string;
  reason: string | null;
  due_on: string | null;
  close_on: string | null;
  since: string | null;
  overdue_days: number;
  sent_on: string | null;
  accepted_on: string | null;
  /** UTC timestamp the acceptance was recorded (connection requests). */
  accepted_at?: string | null;
  /** UTC timestamp: acceptance time + 3 hours, on the message that follows an acceptance. */
  send_after?: string | null;
  planned_for: string | null;
  text: string | null;
  chars: number;
  words: number;
  needs_person: boolean;
  needs_profile: boolean;
  blocked: string | null;
  variants: Record<string, unknown>;
  actions: TouchAction[];
  primary_contact_now: { id: number; name: string } | null;
  updated_at: string | null;
}

export const DUE_GROUPS = [
  "requests_to_send", "requests_to_check", "messages", "follow_ups", "needs", "waiting_for_start", "done_today", "stopped_recently",
] as const;
export type DueGroup = (typeof DUE_GROUPS)[number];

export type ConversationOutcome = "interested" | "redirected" | "not_interested" | "teaming" | "other";
export type ConversationState = "waiting_on_us" | "waiting_on_them" | "not_started" | "closed";
export type AnalysisStatus = "pending" | "done" | "skipped" | "needs_context";
export type Channel = "email" | "linkedin";

export interface ConversationMessage {
  id: number;
  sender: "contact" | "us";
  channel: Channel;
  text: string;
  at: string | null;
  logged_by: string | null;
  source: string;
  analysis_status: AnalysisStatus | null;
  analyzed_at: string | null;
  analysis_note: string | null;
}

/** One conversation with one contact at a company on one RFP. */
export interface Conversation {
  id: number;
  opportunity_id: number;
  rfp_title: string | null;
  prime_company_id: number;
  company_name: string | null;
  prime_contact_id: number | null;
  contact_name: string | null;
  contact_email: string | null;
  redirected_from_id: number | null;
  outcome: ConversationOutcome | null;
  outcome_at: string | null;
  outcome_by: string | null;
  closed_at: string | null;
  state: ConversationState;
  last_sender: "contact" | "us" | null;
  last_channel: Channel | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  message_count: number;
  /** Business days since our last message while we wait on them (null otherwise); `no_answer` from 5. */
  no_answer_days: number | null;
  no_answer: boolean;
  pending_analysis: number;
  created_at: string | null;
  messages?: ConversationMessage[];
}

export interface DueCounts {
  conversations?: number;
  conversations_waiting_on_us?: number;
  conversations_no_answer?: number;
  requests_to_send: number;
  requests_to_check: number;
  messages: number;
  follow_ups: number;
  needs: number;
  waiting_for_start: number;
  overdue: number;
  due_total: number;
}

export interface DueList {
  today: string;
  groups: Record<DueGroup, TouchView[]>;
  coming_up: TouchView[];
  counts: DueCounts;
  /** The open conversations (replies being worked); only the due-list endpoint returns them. */
  conversations?: Conversation[];
}

export interface TouchActionResult { touch: TouchView; path: TouchView[]; warnings: string[] }
export interface RfpTouchGroup { prime_company_id: number; touches: TouchView[] }
export interface ContactTouchGroup { opportunity_id: number; touches: TouchView[] }

function crmApiBase(): string {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

export async function getDueList(opts: { opportunityId?: number; comingUp?: boolean } = {}): Promise<DueList> {
  const params: Record<string, unknown> = {};
  if (opts.opportunityId) params.opportunity_id = opts.opportunityId;
  if (opts.comingUp) params.coming_up = true;
  return (await api.get(`${crmApiBase()}/outreach-touches/due`, { params })).data;
}

export async function getDueCounts(): Promise<DueCounts> {
  return (await api.get(`${crmApiBase()}/outreach-touches/counts`)).data;
}

export async function getRfpTouches(opportunityId: number): Promise<RfpTouchGroup[]> {
  return (await api.get(`${crmApiBase()}/opportunities/${opportunityId}/outreach-touches`)).data;
}

export async function getContactTouches(primeContactId: number): Promise<ContactTouchGroup[]> {
  return (await api.get(`${crmApiBase()}/prime-contacts/${primeContactId}/outreach-touches`)).data;
}

export async function postTouchAction(
  opportunityId: number, touchId: number,
  body: { action: TouchAction; on?: string; note?: string; reason?: string; prime_contact_id?: number; outcome?: ConversationOutcome; received_at?: string },
): Promise<TouchActionResult> {
  return (await api.post(`${crmApiBase()}/opportunities/${opportunityId}/outreach-touches/${touchId}/action`, body)).data;
}

export async function getConversation(id: number): Promise<Conversation> {
  return (await api.get(`${crmApiBase()}/conversations/${id}`)).data;
}

export async function getClosedConversations(opportunityId?: number): Promise<Conversation[]> {
  return (await api.get(`${crmApiBase()}/conversations`, { params: { state: "closed", ...(opportunityId ? { opportunity_id: opportunityId } : {}) } })).data;
}

export async function postConversationMessage(id: number, body: { sender: "contact" | "us"; channel: Channel; text: string; received_at?: string }): Promise<Conversation> {
  return (await api.post(`${crmApiBase()}/conversations/${id}/messages`, body)).data;
}

export async function postConversationOutcome(
  id: number, body: { outcome: ConversationOutcome | null; remove_requested?: boolean; reason?: string },
): Promise<Conversation> {
  return (await api.post(`${crmApiBase()}/conversations/${id}/outcome`, body)).data;
}

export async function postConversationRedirect(id: number, primeContactId: number): Promise<Conversation> {
  return (await api.post(`${crmApiBase()}/conversations/${id}/redirect`, { prime_contact_id: primeContactId })).data;
}

export async function postMessageAnalysis(messageId: number, body: { status: AnalysisStatus; note?: string }): Promise<ConversationMessage> {
  return (await api.post(`${crmApiBase()}/conversations/messages/${messageId}/analysis`, body)).data;
}

/** The due-today page path for the logged-in user type. */
export function dueTodayPath(): string {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm/due-today" : "/employee/crm/due-today";
}

export interface EmailStepContent { to: string | null; email: string | null; subject: string; body: string; personalised: boolean }
export interface LinkedinStepContent {
  to: string | null; title: string | null; email: string | null; profile_url: string | null;
  text: string | null; chars: number; needs_person: boolean; needs_profile: boolean;
}
export interface TimelineStep {
  key: string;
  kind: "email" | "linkedin";
  label: string;
  state: "planned" | "done" | "skipped" | "stopped";
  display?: string;
  reason: string | null;
  date: string | null;
  computed: boolean;
  next?: boolean;
  touch_id?: number;
  accepted_on?: string | null;
  close_on?: string | null;
  /** What the step says: email text (null on a campaign drafted before it was recorded) or the LinkedIn note and who to send it to. */
  content?: EmailStepContent | LinkedinStepContent | null;
}
export interface TimelineCompany {
  prime_company_id: number;
  company_name: string | null;
  contact_name?: string | null;
  contact_email?: string | null;
  replied: boolean;
  steps: TimelineStep[];
  email_floor: string | null;
  locked_until: string | null;
}
export interface RfpTimeline {
  opportunity_id: number;
  today: string;
  started_on: string | null;
  drafted: boolean;
  email_schedule: number[];
  companies: TimelineCompany[];
}

export type OutreachPhase = "waiting_start" | "in_progress" | "replied" | "cooldown";
export interface OutreachStepRef { label: string; date: string | null; kind: "email" | "linkedin" }
/** Where a company is in its ACTIVE outreach (the campaign whose lock still holds it); null on the list when none does. */
export interface CompanyActiveOutreach {
  opportunity_id: number;
  title: string;
  phase: OutreachPhase;
  replied: boolean;
  locked_until: string;
  email_floor: string | null;
  next_step: OutreachStepRef | null;
  last_done: OutreachStepRef | null;
  timeline?: TimelineCompany;      // the company page only
}
export interface CompanyOutreachHistoryItem { opportunity_id: number; title: string; drafted_on: string | null; started_on: string | null; replied: boolean }
export interface CompanyOutreachDetail { active: CompanyActiveOutreach | null; history: CompanyOutreachHistoryItem[] }

/** One call per RFP (optionally one company): the six steps per company in date order, plus the company lock. */
export async function getRfpTimeline(opportunityId: number, companyId?: number): Promise<RfpTimeline> {
  return (await api.get(`${crmApiBase()}/opportunities/${opportunityId}/outreach-timeline`, { params: companyId ? { company_id: companyId } : {} })).data;
}

// ── the Pipeline: every LinkedIn path, one row each ──────────────────────────────────────────────────────────────────
export const PATH_STATES = ["not_started", "to_send", "waiting", "accepted", "messaged", "followed_up", "replied", "closed", "skipped"] as const;
export type PathState = (typeof PATH_STATES)[number];
export type StepStatus = "done" | "pending" | "dropped";
export interface PathStep { status: StepStatus; on: string | null }

export interface OutreachPath {
  key: string;
  opportunity_id: number;
  rfp_title: string | null;
  prime_company_id: number;
  company_name: string | null;
  prime_contact_id: number | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_title: string | null;
  profile_url: string | null;
  state: PathState;
  reason: string | null;
  since: string | null;
  days_in_state: number | null;
  last_activity_on: string | null;
  steps: { request: PathStep; accepted: PathStep; message: PathStep; follow_up: PathStep; replied: PathStep };
  last_action: { action: string; at: string | null; actor: string; actor_label: string } | null;
  conversation: { conversation_id: number; outcome: ConversationOutcome | null; open: boolean } | null;
  touch_ids: number[];
}

export interface OutreachPathList {
  today: string;
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  items: OutreachPath[];
  state_counts: Record<PathState, number>;
  rfps: { id: number; title: string | null }[];
  actors: { key: string; label: string }[];
}

export interface OutreachPathQuery {
  opportunityId?: number; state?: PathState; search?: string; actor?: string; fromDate?: string; toDate?: string;
  sort?: "last_action" | "days_in_state" | "company" | "rfp"; direction?: "asc" | "desc"; page?: number; pageSize?: number;
}

export async function getOutreachPaths(q: OutreachPathQuery = {}): Promise<OutreachPathList> {
  const params: Record<string, unknown> = {};
  if (q.opportunityId) params.opportunity_id = q.opportunityId;
  if (q.state) params.state = q.state;
  if (q.search?.trim()) params.search = q.search.trim();
  if (q.actor) params.actor = q.actor;
  if (q.fromDate) params.from_date = q.fromDate;
  if (q.toDate) params.to_date = q.toDate;
  if (q.sort) params.sort = q.sort;
  if (q.direction) params.direction = q.direction;
  if (q.page) params.page = q.page;
  if (q.pageSize) params.page_size = q.pageSize;
  return (await api.get(`${crmApiBase()}/outreach-paths`, { params })).data;
}

/** One row of a Performance breakdown (by RFP, week or person). */
export interface StatsRow {
  requests_sent: number;
  accepted: number;
  acceptance_rate: number | null;
  acceptance_matured: number;
  acceptance_matured_accepted: number;
  replied: number;
  reply_rate: number | null;
}
export interface StatsHeadline {
  requests_sent: number; accepted: number; replied: number; positive: number;
  acceptance_rate: number | null; reply_rate: number | null; median_hours: number | null; dead_ends: number;
}
export interface ReplyStats {
  started: number;
  outcomes: Record<"interested" | "teaming" | "not_interested" | "redirected" | "other" | "none", number>;
  response: { count: number; median_hours: number | null; p90_hours: number | null };
  waiting_on_us: { count: number; oldest_days: number | null };
}
export interface KeepingUp {
  on_time: { sent: number; on_time: number; late: number; rate: number | null; median_days_late: number | null; by_kind: { kind: string; label: string; sent: number; on_time: number; rate: number | null }[] };
  overdue_now: { total: number; oldest_days: number | null; buckets: { label: string; count: number }[] };
  waiting_now: { total: number; buckets: { label: string; count: number }[] };
}
export interface SendSlot { weekday: number; band: number; sent: number; matured: number; accepted: number; rate: number | null }
export interface SendTimes { bands: string[]; cells: SendSlot[]; min_matured: number; best: SendSlot | null }
export interface OutreachStats {
  send_times: SendTimes;
  keeping_up: KeepingUp;
  current: StatsHeadline;
  previous: StatsHeadline & { from_date: string; to_date: string };
  replies: ReplyStats;
  from_date: string;
  to_date: string;
  today: string;
  accept_window_days: number;
  funnel: { requests_sent: number; accepted: number; message_sent: number; follow_up_sent: number; replied: number; positive: number };
  acceptance_rate: number | null;
  acceptance_matured: number;
  acceptance_matured_accepted: number;
  reply_rate: number | null;
  time_to_accept: { count: number; median_hours: number | null; p90_hours: number | null; buckets: { label: string; count: number }[] };
  dead_ends: { no_profile: number; closed_unaccepted: number; stopped_by_reply: number; total: number; shares: Record<"no_profile" | "closed_unaccepted" | "stopped_by_reply", number | null> };
  by_rfp: (StatsRow & { opportunity_id: number; rfp_title: string | null })[];
  by_week: (StatsRow & { week_start: string })[];
  by_actor: (StatsRow & { actor: string; label: string })[];
  by_role: (StatsRow & { label: string })[];
  by_size: (StatsRow & { label: string })[];
  by_state: (StatsRow & { label: string })[];
}

export async function getOutreachStats(q: { fromDate?: string; toDate?: string } = {}): Promise<OutreachStats> {
  const params: Record<string, unknown> = {};
  if (q.fromDate) params.from_date = q.fromDate;
  if (q.toDate) params.to_date = q.toDate;
  return (await api.get(`${crmApiBase()}/outreach-stats`, { params })).data;
}

/** One entry of a path's activity feed: a single action, who made it and when. */
export interface PathActivityItem {
  group: string;
  at: string | null;
  action: string;
  text: string;
  /** `person`: something a person did (or undid); `system`: the app's own bookkeeping (plan, dates, stops). */
  kind: "person" | "system";
  actor: string;
  actor_label: string;
  touch_kinds: string[];
  on: string | null;
  back_dated: boolean;
  note: string | null;
  undone: boolean;
  effects: string[];
}
export interface PathActivity { opportunity_id: number; prime_company_id: number; count: number; items: PathActivityItem[] }

export async function getPathActivity(opportunityId: number, companyId: number): Promise<PathActivity> {
  return (await api.get(`${crmApiBase()}/opportunities/${opportunityId}/outreach-activity`, { params: { company_id: companyId } })).data;
}

/** The Conversations inbox: every conversation, open and closed, filtered and paged. `counts` ignore the state, outcome and waiting filters. */
export interface ConversationInbox {
  items: Conversation[];
  total_count: number;
  total_pages: number;
  page: number;
  page_size: number;
  counts: { all: number; open: number; closed: number; waiting_on_us: number; waiting_on_them: number; no_answer: number };
  rfps: { id: number; title: string | null }[];
}

export async function getConversationInbox(q: {
  state?: "open" | "closed" | "all"; outcome?: ConversationOutcome | "none"; waitingOn?: "us" | "them"; noAnswer?: boolean;
  opportunityId?: number; search?: string; page?: number; pageSize?: number;
}): Promise<ConversationInbox> {
  const params: Record<string, string | number | boolean> = {};
  if (q.state) params.state = q.state;
  if (q.outcome) params.outcome = q.outcome;
  if (q.waitingOn) params.waiting_on = q.waitingOn;
  if (q.noAnswer) params.no_answer = true;
  if (q.opportunityId) params.opportunity_id = q.opportunityId;
  if (q.search) params.search = q.search;
  if (q.page) params.page = q.page;
  if (q.pageSize) params.page_size = q.pageSize;
  return (await api.get(`${crmApiBase()}/conversations/inbox`, { params })).data;
}
